import StoreUpdateEvents from "../constants/events/StoreUpdateEvents";
import SecureBaseStore from "./SecureBaseStore";
import UIInteractionEvents from "../constants/events/UIInteractionEvents";
import ActionResultEvents from "../constants/events/ActionResultEvents";
import SavingStates from "../constants/states/SavingStates";
import {FormFieldWithValidityState, FormFieldWithValidityAndUniqueness} from "./FormFieldWithValidityState";


class ProfileEditViewState {
    constructor(
        savingState,
        displayName,
        email,
        phoneNumber,
        about,
        paymentAddressName,
        currentPaymentAddressGooglePlaceId,
        currentPaymentAddressStreetNumber,
        currentPaymentAddressRoute,
        currentPaymentAddressSecondaryAddress,
        currentPaymentAddressLocality,
        currentPaymentAddressAdministrativeAreaLevel1,
        currentPaymentAddressAdministrativeAreaLevel2,
        currentPaymentAddressCountryLong,
        currentPaymentAddressCountryShort,
        currentPaymentAddressPostalCode,
        formattedAddress
    ) {
        this.savingState = savingState;
        this.displayName = displayName;
        this.email = email;
        this.phoneNumber = phoneNumber;
        this.about = about;
        this.paymentAddressName = paymentAddressName;
        this.currentPaymentAddressGooglePlaceId = currentPaymentAddressGooglePlaceId;
        this.currentPaymentAddressStreetNumber = currentPaymentAddressStreetNumber;
        this.currentPaymentAddressRoute = currentPaymentAddressRoute;
        this.currentPaymentAddressSecondaryAddress = currentPaymentAddressSecondaryAddress;
        this.currentPaymentAddressLocality = currentPaymentAddressLocality;
        this.currentPaymentAddressAdministrativeAreaLevel1 = currentPaymentAddressAdministrativeAreaLevel1;
        this.currentPaymentAddressAdministrativeAreaLevel2 = currentPaymentAddressAdministrativeAreaLevel2;
        this.currentPaymentAddressCountryLong = currentPaymentAddressCountryLong;
        this.currentPaymentAddressCountryShort = currentPaymentAddressCountryShort;
        this.currentPaymentAddressPostalCode = currentPaymentAddressPostalCode;
        this.formattedAddress = formattedAddress;
    }

    equals(other) {
        return this.savingState === other.savingState &&
            this.displayName.equals(other.displayName) &&
            this.email.equals(other.email) &&
            this.phoneNumber.equals(other.phoneNumber) &&
            this.about.equals(other.about) &&
            this.paymentAddressName.equals(other.paymentAddressName) &&
            this.currentPaymentAddressGooglePlaceId.equals(other.currentPaymentAddressGooglePlaceId) &&
            this.currentPaymentAddressStreetNumber === other.currentPaymentAddressStreetNumber &&
            this.currentPaymentAddressRoute === other.currentPaymentAddressRoute &&
            this.currentPaymentAddressSecondaryAddress.equals(other.currentPaymentAddressSecondaryAddress) &&
            this.currentPaymentAddressLocality === other.currentPaymentAddressLocality &&
            this.currentPaymentAddressAdministrativeAreaLevel1 === other.currentPaymentAddressAdministrativeAreaLevel1 &&
            this.currentPaymentAddressAdministrativeAreaLevel2 === other.currentPaymentAddressAdministrativeAreaLevel2 &&
            this.currentPaymentAddressCountryLong === other.currentPaymentAddressCountryLong &&
            this.currentPaymentAddressCountryShort === other.currentPaymentAddressCountryShort &&
            this.currentPaymentAddressPostalCode === other.currentPaymentAddressPostalCode &&
            this.formattedAddress === other.formattedAddress;
    }
}

class ImmutableProfileEditViewStateBuilder {

    constructor() {
        this.savingState = SavingStates.NOT_STARTED;
        this.displayName = FormFieldWithValidityAndUniqueness.initialDefaults("");
        this.email = FormFieldWithValidityAndUniqueness.initialDefaults("");
        this.phoneNumber = FormFieldWithValidityState.initialDefaults("");
        this.about = new FormFieldWithValidityState("", true, false);
        this.paymentAddressName = FormFieldWithValidityState.initialDefaults("");
        this.currentPaymentAddressGooglePlaceId = FormFieldWithValidityState.initialDefaults("");
        this.currentPaymentAddressStreetNumber = "";
        this.currentPaymentAddressRoute = "";
        this.currentPaymentAddressSecondaryAddress = new FormFieldWithValidityState("", true, false);
        this.currentPaymentAddressLocality = "";
        this.currentPaymentAddressAdministrativeAreaLevel1 = "";
        this.currentPaymentAddressAdministrativeAreaLevel2 = "";
        this.currentPaymentAddressCountryLong = "";
        this.currentPaymentAddressCountryShort = "";
        this.currentPaymentAddressPostalCode = "";
        this.formattedAddress = "";
    }

    static fromState(state) {
        return new ImmutableProfileEditViewStateBuilder()
            .setSavingState(state.savingState)
            .setDisplayName(state.displayName.currentValue, state.displayName.isValid, state.displayName.isTouched, state.displayName.isUnique)
            .setEmail(state.email.currentValue, state.email.isValid, state.email.isTouched, state.email.isUnique)
            .setPhoneNumber(state.phoneNumber.currentValue, state.phoneNumber.isValid, state.phoneNumber.isTouched)
            .setAbout(state.about.currentValue, state.about.isValid, state.about.isTouched)
            .setPaymentAddressName(state.paymentAddressName.currentValue, state.paymentAddressName.isValid, state.paymentAddressName.isTouched)
            .setCurrentPaymentAddressGooglePlaceId(state.currentPaymentAddressGooglePlaceId.currentValue, state.currentPaymentAddressGooglePlaceId.isValid, state.currentPaymentAddressGooglePlaceId.isTouched)
            .setCurrentPaymentAddressStreetNumber(state.currentPaymentAddressStreetNumber)
            .setCurrentPaymentAddressRoute(state.currentPaymentAddressRoute)
            .setCurrentPaymentAddressSecondaryAddress(state.currentPaymentAddressSecondaryAddress.currentValue, state.currentPaymentAddressSecondaryAddress.isValid, state.currentPaymentAddressSecondaryAddress.isTouched)
            .setCurrentPaymentAddressLocality(state.currentPaymentAddressLocality)
            .setCurrentPaymentAddressAdministrativeAreaLevel1(state.currentPaymentAddressAdministrativeAreaLevel1)
            .setCurrentPaymentAddressAdministrativeAreaLevel2(state.currentPaymentAddressAdministrativeAreaLevel2)
            .setCurrentPaymentAddressCountryLong(state.currentPaymentAddressCountryLong)
            .setCurrentPaymentAddressCountryShort(state.currentPaymentAddressCountryShort)
            .setCurrentPaymentAddressPostalCode(state.currentPaymentAddressPostalCode)
            .setFormattedAddress(state.formattedAddress);
    }

    setSavingState(newState) {
        this.savingState = newState;
        return this;
    }

    setDisplayName(displayName, isValid, isTouched, isUnique) {
        this.displayName = new FormFieldWithValidityAndUniqueness(displayName, isValid, isTouched, isUnique);
        return this;
    }

    setEmail(email, isValid, isTouched, isUnique) {
        this.email = new FormFieldWithValidityAndUniqueness(email, isValid, isTouched, isUnique);
        return this;
    }

    setPhoneNumber(phoneNumber, isValid, isTouched) {
        this.phoneNumber = new FormFieldWithValidityState(phoneNumber, isValid, isTouched);
        return this;
    }

    setAbout(about, isValid, isTouched) {
        this.about = new FormFieldWithValidityState(about, isValid, isTouched);
        return this;
    }

    setPaymentAddressName(paymentAddressName, isValid, isTouched) {
        this.paymentAddressName = new FormFieldWithValidityState(paymentAddressName, isValid, isTouched);
        return this;
    }

    setCurrentPaymentAddressGooglePlaceId(currentPaymentAddressGooglePlaceId, isValid, isTouched) {
        this.currentPaymentAddressGooglePlaceId = new FormFieldWithValidityState(currentPaymentAddressGooglePlaceId, isValid, isTouched);
        return this;
    }

    setCurrentPaymentAddressStreetNumber(currentPaymentAddressStreetNumber) {
        this.currentPaymentAddressStreetNumber = currentPaymentAddressStreetNumber;
        return this;
    }

    setCurrentPaymentAddressRoute(currentPaymentAddressRoute) {
        this.currentPaymentAddressRoute = currentPaymentAddressRoute;
        return this;
    }

    setCurrentPaymentAddressSecondaryAddress(currentPaymentAddressSecondaryAddress, isValid, isTouched) {
        this.currentPaymentAddressSecondaryAddress = new FormFieldWithValidityState(currentPaymentAddressSecondaryAddress, isValid, isTouched);
        return this;
    }

    setCurrentPaymentAddressLocality(currentPaymentAddressLocality) {
        this.currentPaymentAddressLocality = currentPaymentAddressLocality;
        return this;
    }

    setCurrentPaymentAddressAdministrativeAreaLevel1(currentPaymentAddressAdministrativeAreaLevel1) {
        this.currentPaymentAddressAdministrativeAreaLevel1 = currentPaymentAddressAdministrativeAreaLevel1;
        return this;
    }

    setCurrentPaymentAddressAdministrativeAreaLevel2(currentPaymentAddressAdministrativeAreaLevel2) {
        this.currentPaymentAddressAdministrativeAreaLevel2 = currentPaymentAddressAdministrativeAreaLevel2;
        return this;
    }

    setCurrentPaymentAddressCountryLong(currentPaymentAddressCountryLong) {
        this.currentPaymentAddressCountryLong = currentPaymentAddressCountryLong;
        return this;
    }

    setCurrentPaymentAddressCountryShort(currentPaymentAddressCountryShort) {
        this.currentPaymentAddressCountryShort = currentPaymentAddressCountryShort;
        return this;
    }

    setCurrentPaymentAddressPostalCode(currentPaymentAddressPostalCode) {
        this.currentPaymentAddressPostalCode = currentPaymentAddressPostalCode;
        return this;
    }

    setFormattedAddress(formattedAddress) {
        this.formattedAddress = formattedAddress;
        return this;
    }

    build() {
        return Object.freeze(
            new ProfileEditViewState(
                this.savingState,
                this.displayName,
                this.email,
                this.phoneNumber,
                this.about,
                this.paymentAddressName,
                this.currentPaymentAddressGooglePlaceId,
                this.currentPaymentAddressStreetNumber,
                this.currentPaymentAddressRoute,
                this.currentPaymentAddressSecondaryAddress,
                this.currentPaymentAddressLocality,
                this.currentPaymentAddressAdministrativeAreaLevel1,
                this.currentPaymentAddressAdministrativeAreaLevel2,
                this.currentPaymentAddressCountryLong,
                this.currentPaymentAddressCountryShort,
                this.currentPaymentAddressPostalCode,
                this.formattedAddress));
    }
}


class ProfileEditViewStore extends SecureBaseStore {

    constructor() {
        super(StoreUpdateEvents.PROFILE_EDIT_VIEW_STORE_UPDATED, ImmutableProfileEditViewStateBuilder);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_DISPLAY_NAME_CHANGED, this.handleDisplayNameChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_EMAIL_CHANGED, this.handleEmailChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_PHONE_NUMBER_CHANGED, this.handlePhoneNumberChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_ABOUT_CHANGED, this.handleAboutChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_PAYMENT_ADDRESS_NAME_CHANGED, this.handlePaymentAddressNameChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_ADDRESS_AUTOCOMPLETE_CHANGED, this.handleGoogleAutocompleteChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_SECONDARY_ADDRESS_CHANGED, this.handleCurrentPaymentAddressSecondaryAddressChanged);
        this.subscribeToEvent(UIInteractionEvents.PROFILE_EDIT_SAVE_SUBMITTED, this.handleSaveStarted);
        this.subscribeToEvent(ActionResultEvents.PROFILE_EDIT_NON_UNIQUE_DISPLAY_NAME, this.handleNonUniqueDisplayName);
        this.subscribeToEvent(ActionResultEvents.PROFILE_EDIT_NON_UNIQUE_EMAIL, this.handleNonUniqueEmail);
        this.subscribeToEvent(ActionResultEvents.PROFILE_EDITED, this.resetStore);
        this.subscribeToEvent(ActionResultEvents.PROFILE_LOADED, this.handleProfileLoaded)
    }

    handleSaveStarted = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setSavingState(SavingStates.SAVING)
            .build();
    };

    handleProfileLoaded = (topic, data) => {
        return new ImmutableProfileEditViewStateBuilder()
            .setDisplayName(data.displayName, true, false, true)
            .setEmail(data.email, true, false, true)
            .setAbout(data.about, true, false)
            .setPhoneNumber(data.phoneNumber, true, false)
            .setCurrentPaymentAddressGooglePlaceId(data.currentPaymentAddressGooglePlaceId, true, false)
            .setCurrentPaymentAddressStreetNumber(data.currentPaymentAddressStreetNumber)
            .setCurrentPaymentAddressRoute(data.currentPaymentAddressRoute)
            .setCurrentPaymentAddressLocality(data.currentPaymentAddressLocality)
            .setCurrentPaymentAddressAdministrativeAreaLevel1(data.currentPaymentAddressAdministrativeAreaLevel1)
            .setCurrentPaymentAddressAdministrativeAreaLevel2(data.currentPaymentAddressAdministrativeAreaLevel2)
            .setCurrentPaymentAddressCountryLong(data.currentPaymentAddressCountryLong)
            .setCurrentPaymentAddressCountryShort(data.currentPaymentAddressCountryShort)
            .setCurrentPaymentAddressPostalCode(data.currentPaymentAddressPostalCode)
            .setCurrentPaymentAddressSecondaryAddress(data.currentPaymentAddressSecondaryAddress, true, false)
            .setPaymentAddressName(data.paymentAddressName, true, false)
            .setFormattedAddress(data.currentPaymentAddressFormattedAddress)
            .build();
    };

    handleNonUniqueDisplayName = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setDisplayName(this.state.displayName.currentValue, false, true, false)
            .setSavingState(SavingStates.NOT_STARTED)
            .build();
    };

    handleNonUniqueEmail = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setEmail(this.state.email.currentValue, false, true, false)
            .setSavingState(SavingStates.NOT_STARTED)
            .build();
    };

    handleDisplayNameChanged = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setDisplayName(data.displayName, data.isValid, true, true)
            .build();
    };

    handleEmailChanged = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setEmail(data.email, data.isValid, true, true)
            .build();
    };

    handlePhoneNumberChanged = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setPhoneNumber(data.phoneNumber, data.isValid, true)
            .build();
    };

    handleAboutChanged = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setAbout(data.about, data.isValid, true)
            .build();
    };

    handlePaymentAddressNameChanged = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setPaymentAddressName(data.paymentAddressName, data.isValid, true)
            .build();
    };

    handleCurrentPaymentAddressSecondaryAddressChanged = (topic, data) => {
        return ImmutableProfileEditViewStateBuilder.fromState(this.state)
            .setCurrentPaymentAddressSecondaryAddress(data.currentPaymentAddressSecondaryAddress, data.isValid, true)
            .build();
    };

    handleGoogleAutocompleteChanged = (topic, data) => {
        const builder =
            ImmutableProfileEditViewStateBuilder.fromState(this.state)
                .setCurrentPaymentAddressGooglePlaceId(data.address.place_id, data.isValid, true);

        for (const addressComponent of data.address.address_components) {
            const type = addressComponent.types[0];
            switch (type) {
                case "street_number":
                    builder.setCurrentPaymentAddressStreetNumber(addressComponent.long_name);
                    break;
                case "route":
                    builder.setCurrentPaymentAddressRoute(addressComponent.long_name);
                    break;
                case "locality":
                    builder.setCurrentPaymentAddressLocality(addressComponent.long_name);
                    break;
                case "administrative_area_level_1":
                    builder.setCurrentPaymentAddressAdministrativeAreaLevel1(addressComponent.long_name);
                    break;
                case "administrative_area_level_2":
                    builder.setCurrentPaymentAddressAdministrativeAreaLevel2(addressComponent.long_name);
                    break;
                case "country":
                    builder.setCurrentPaymentAddressCountryLong(addressComponent.long_name);
                    builder.setCurrentPaymentAddressCountryShort(addressComponent.short_name);
                    break;
                case "postal_code":
                    builder.setCurrentPaymentAddressPostalCode(addressComponent.long_name);
                    break;
                default:
                    break;
            }
        }
        return builder.build();
    };



}

export default new ProfileEditViewStore();